import {Button, Changed, Icon, Spinner} from '@bitstillery/common/components/portal'
import {classes, present} from '@bitstillery/common/lib/utils'
import {MithrilComponent} from '@bitstillery/mithril'
import {format_money_with_symbol} from '@bitstillery/common/lib/format'
import {$t, events} from '@bitstillery/common/app'
import m from '@bitstillery/mithril'
import {AvailabilityType, CartValidation} from '@bitstillery/common/api/fact2server'
import type {State} from '@bitstillery/mithril'
import type {SalesOrderItemResponse, SalesOrderTBOItemResponse} from '@bitstillery/common/api/fact2server'

import {$m, $s} from '@/app'
import type {CartValidationError} from '@/lib/state'
import type {CartItemState, CartOfferItemLookup} from '@/models/cart'
import {filters} from '@/components/pages/offers/offer_list'
import {is_string_list} from '@/lib/filter_selection'
import {CartItemActions, open_actions} from '@/components/panels/panel_cart/cart_item_actions'
import {status_icon_entry_for_type} from '@/components/collection/product/product'

/** A booked sales order item, or a cart line from `$s.cart.items`; `is_cart` tells them apart. */
type OrderLineArgs =
    | [order_item: State<CartItemState>, is_cart: true, auto_scroll?: boolean]
    | [order_item: SalesOrderItemResponse | SalesOrderTBOItemResponse, is_cart: false, auto_scroll?: boolean]

interface OrderItemsAttrs {
    // `wide` enables the spacious, side-by-side checkout layout; the cart
    // panel uses the default compact list.
    variant?: 'wide'
}

export class OrderItems extends MithrilComponent<OrderItemsAttrs> {
    /** The cart line we last smooth-scrolled into view, so a single change scrolls once (not every redraw). */
    scrolled_key: number | string | null = null

    /**
     * Bring a freshly-changed cart line into the cart's scroll area. When an item is modified from the
     * product list its row may sit outside the visible cart, so the highlight alone isn't seen — scroll
     * it in, once per change.
     *
     * Only for the cart panel, which the user is not looking at while they change an item. `scrollIntoView`
     * moves every scrollable ancestor, so on a surface where the list shares its scroll container with what
     * the user is clicking, this drags the page away from under them — see the `auto_scroll` argument of
     * `order_line`.
     */
    scroll_changed_into_view(dom: Element, key: number | string) {
        if (this.scrolled_key === key) {
            return
        }
        this.scrolled_key = key
        dom.scrollIntoView({behavior: 'smooth', block: 'nearest'})
    }

    /**
     * Interpolation for a line's tooltip. The count must be the same number the `.quantity` column shows —
     * `number_of_cases` is only that when the user prices per case; per bottle the row shows the bottle
     * total, so a raw case count read as "1 bottle" next to a row saying "6".
     */
    line_tip_params(order_item: Pick<SalesOrderItemResponse, 'case_number_of_bottles' | 'number_of_cases' | 'product_name'>) {
        const count = $m.offer.unit_amount(order_item.number_of_cases, order_item.case_number_of_bottles)
        return {
            count,
            product: order_item.product_name,
            units: $t(`unit.${$s.identity.user.price_preference}`, {count}),
        }
    }

    booked_item_tip(order_item: SalesOrderItemResponse | SalesOrderTBOItemResponse) {
        if (order_item.offer_item_type === AvailabilityType.Tbo) {
            return $t('cart.booked.tbo_tip', this.line_tip_params(order_item))
        }
        return $t('cart.booked.stock_tip', this.line_tip_params(order_item))
    }

    cart_item_tip(cart_item: State<CartItemState>) {
        const {errors} = $s.cart
        const quantity_error = errors[CartValidation.QUANTITY][cart_item.case_artkey]
        const price_error = errors[CartValidation.PRICE_WAS_RAISED][cart_item.case_artkey]
        const min_quantity_error = errors[CartValidation.MIN_QUANTITY][cart_item.case_artkey]
        const step_size_error = errors[CartValidation.STEP_SIZE][cart_item.case_artkey]
        if (quantity_error) {
            if (quantity_error.valid_value === 0) {
                return `${$t('cart.unavailable')} ${$t('cart.unavailable_action')}`
            }
            const count = $m.offer.unit_amount(quantity_error.valid_value, cart_item.case_number_of_bottles)
            return `${$t('cart.less_available', {
                count,
                units: $t(`unit.${$s.identity.user.price_preference}`, {count}),
            })} ${$t('cart.error_category.action.QUANTITY')}`
        } else if (price_error) {
            return $t('cart.price_changed', {
                currency: $s.identity.user.currency,
                new_price: $m.offer.unit_price(price_error.valid_value, cart_item.case_number_of_bottles),
                old_price: $m.offer.unit_price(price_error.invalid_value, cart_item.case_number_of_bottles),
            })
        } else if (min_quantity_error) {
            // Todo; share order-quantity computed methods with model
            const count = $m.offer.unit_amount(min_quantity_error.valid_value, cart_item.case_number_of_bottles)
            return `${$t('cart.min_quantity', {
                count,
                units: $t(`unit.${$s.identity.user.price_preference}`, {count}),
            })} ${$t('cart.error_category.action.QUANTITY')}`
        } else if (step_size_error) {
            return `${$t('cart.STEP_SIZE', {
                count: step_size_error.valid_value,
                units: $t(`unit.${$s.identity.user.price_preference}`, {
                    count: step_size_error.valid_value,
                }),
            })} ${$t('cart.error_category.action.QUANTITY')}`
        }
        if (cart_item.offer_item_type === AvailabilityType.Tbo) {
            return $t('cart.in_cart.tbo_tip', this.line_tip_params(cart_item))
        } else if (cart_item.offer_item_type === AvailabilityType.Purchase) {
            return $t('cart.in_cart.purchase_tip', this.line_tip_params(cart_item))
        }
        return $t('cart.in_cart.stock_tip', this.line_tip_params(cart_item))
    }

    /**
     * Common order line renderer. An order_item can be a sales_order_item
     * or a cart_item; e.g. an entry from $s.cart.items. Only the latter has
     * validation logic, because sales_order_items have already been booked.
     *
     * @param auto_scroll - Whether a changed line may scroll itself into view. Off on the checkout, where
     *   the list and the recommendation carousel sit in one scroll container: adding a product from the
     *   carousel would scroll the new cart line into view and take the carousel off screen mid-click. The
     *   list is on screen there anyway, so the `cart-item-changed` highlight carries the feedback alone.
     */
    order_line(...[order_item, is_cart, auto_scroll = true]: OrderLineArgs) {
        // Keyed by case artkey, and a line without an error has no entry.
        const errors: Record<CartValidation, Record<string, CartValidationError | undefined>> = $s.cart.errors
        // Once the highlight has cleared, allow the next change (even to the same line) to scroll again.
        if ($s.cart.item_changed === null) {
            this.scrolled_key = null
        }
        const is_changed = is_cart && order_item.case_artkey === $s.cart.item_changed
        const _classes: Parameters<typeof classes> = ['product-line']
        if (is_cart) {
            const cart_item = order_item
            _classes.push({
                // Reading the shared overlay-open signal here subscribes this list to it, so the row
                // re-renders (and hides its in-flow price — see cart_item_actions.css) the moment the
                // action overlay expands, without relying on `:has()`.
                'actions-open': open_actions.key === cart_item.case_artkey,
                'cart-item-changed': is_changed,
                // Only a cart line carries the action overlay (a booked line is already ordered), so the
                // adjust affordances — the revealed pill, the lit quantity — key off this, not `.product-line`.
                'cart-line': true,
                warning:
                    errors[CartValidation.QUANTITY][cart_item.case_artkey] ||
                    errors[CartValidation.PRICE_WAS_RAISED][cart_item.case_artkey] ||
                    errors[CartValidation.MIN_QUANTITY][cart_item.case_artkey],
            })
        }

        const itemKey = order_item.case_artkey

        // The line's price/subtotal. Rendered in the `.costs` column AND handed to the action overlay, so
        // the overlay can show it next to the buttons on reveal (it floats over the in-flow price).
        const render_price = () => {
            let subtotal_price

            if (is_cart) {
                const cart_item = order_item
                const offer_items: CartOfferItemLookup = $s.cart.offer_items
                const offer_item = offer_items[cart_item.case_artkey]
                if (!offer_item) {
                    return
                }

                const offer_item_price = $m.offer.offer_item_price(offer_item, $s.cart.errors)
                const unit_amount = $m.offer.unit_amount(cart_item.number_of_cases, cart_item.case_number_of_bottles)
                subtotal_price = unit_amount * offer_item_price.current
                if (cart_item.case_artkey in errors[CartValidation.PRICE_WAS_RAISED]) {
                    const price_error = present(
                        errors[CartValidation.PRICE_WAS_RAISED][cart_item.case_artkey],
                        'price raised error',
                    )
                    subtotal_price = cart_item.number_of_cases * price_error.invalid_value
                    const new_price = cart_item.number_of_cases * price_error.valid_value

                    return <Changed previous={subtotal_price} current={new_price} type='warning' />
                }

                return format_money_with_symbol(subtotal_price, offer_item.currency)
            }
            subtotal_price = order_item.number_of_cases * Number(order_item.price_per_case)
            // Reserved items: show currency explicitly from the order item (preferred)
            return format_money_with_symbol(subtotal_price, $s.identity.user.currency)
        }
        return (
            <div
                className={classes(_classes)}
                key={itemKey}
                oncreate={(vnode: m.VnodeDOM<unknown>) => {
                    if (is_changed && auto_scroll) {
                        this.scroll_changed_into_view(vnode.dom, order_item.case_artkey)
                    }
                }}
                onupdate={(vnode: m.VnodeDOM<unknown>) => {
                    if (is_changed && auto_scroll) {
                        this.scroll_changed_into_view(vnode.dom, order_item.case_artkey)
                    }
                }}
            >
                {(() => {
                    let icon_name = $s.identity.user.price_preference as string
                    if (
                        is_cart &&
                        (errors[CartValidation.MIN_QUANTITY][order_item.case_artkey] ||
                            errors[CartValidation.PRICE_WAS_RAISED][order_item.case_artkey] ||
                            errors[CartValidation.QUANTITY][order_item.case_artkey] ||
                            errors[CartValidation.STEP_SIZE][order_item.case_artkey])
                    ) {
                        if (order_item.case_artkey in errors[CartValidation.QUANTITY]) {
                            icon_name = 'stock'
                        } else if (
                            order_item.case_artkey in errors[CartValidation.MIN_QUANTITY] ||
                            order_item.case_artkey in errors[CartValidation.STEP_SIZE]
                        ) {
                            icon_name = 'trolly'
                        } else if (order_item.case_artkey in errors[CartValidation.PRICE_WAS_RAISED]) {
                            icon_name = 'money'
                        } else {
                            icon_name = 'trolly'
                        }
                    } else {
                        icon_name = status_icon_entry_for_type(order_item.offer_item_type).icon
                    }

                    return (
                        <Icon
                            className='quantity-type-icon'
                            name={icon_name}
                            size='s'
                            tip={() => (is_cart ? this.cart_item_tip(order_item) : this.booked_item_tip(order_item))}
                            type='default'
                        />
                    )
                })()}
                <div className='quantity'>
                    {$m.offer.unit_amount(order_item.number_of_cases, order_item.case_number_of_bottles)}
                </div>

                <div
                    className='product-name'
                    onclick={() => {
                        if (m.route.get().startsWith('/shop/checkout')) {
                            return
                        }

                        filters.offertype.selection = ''
                        const terms = filters.search.selection
                        if (Array.isArray(terms) && is_string_list(terms)) {
                            terms.splice(0, terms.length, order_item.product_name)
                        }
                        events.emit('search', filters.search.selection)
                    }}
                >
                    {order_item.product_name}
                </div>

                <div className='costs'>{render_price()}</div>

                {(() => {
                    if (!is_cart) {
                        return
                    }
                    const cart_item = order_item

                    const error =
                        errors[CartValidation.QUANTITY][cart_item.case_artkey] ||
                        errors[CartValidation.PRICE_WAS_RAISED][cart_item.case_artkey] ||
                        errors[CartValidation.MIN_QUANTITY][cart_item.case_artkey] ||
                        errors[CartValidation.STEP_SIZE][cart_item.case_artkey]
                    return <CartItemActions cart_item={cart_item} error={error} price={render_price} />
                })()}
            </div>
        )
    }

    view(vnode: m.Vnode<OrderItemsAttrs>) {
        const booked_items = [...$s.cart.sales_order_items, ...$s.cart.sales_order_tbo_items]
        const cart_items = Object.values($s.cart.items)
            // Consider only items without errors for counts and rendering here
            .filter(
                (cart_item) =>
                    cart_item._invisible !== true &&
                    !(cart_item.case_artkey in $s.cart.errors[CartValidation.QUANTITY]) &&
                    !(cart_item.case_artkey in $s.cart.errors[CartValidation.PRICE_WAS_RAISED]) &&
                    !(cart_item.case_artkey in $s.cart.errors[CartValidation.MIN_QUANTITY]) &&
                    !(cart_item.case_artkey in $s.cart.errors[CartValidation.STEP_SIZE]),
            )
        return (
            <div className={classes('c-order-items', {wide: vnode.attrs.variant === 'wide'})}>
                {booked_items.length > 0 && (
                    <div className='order-section'>
                        <div className='break-line'>
                            <Icon className='quantity-type-icon' name='booked' type='info' />
                            {$t('cart.soi.booked_title', {booked_items: booked_items.length})}
                        </div>
                        {booked_items.map((sales_order_item) => this.order_line(sales_order_item, false))}
                    </div>
                )}
                <div className='order-section'>
                    <div className='break-line'>
                        <Icon className='quantity-type-icon' name='cartCheck' type='unset' />
                        {$t('cart.header_text', {cart_items: cart_items.length})}
                    </div>
                    {cart_items.length === 0 ? (
                        <div className='empty-cart'>
                            {(() => {
                                if ($s.cart.loading && !$s.cart.artkey) {
                                    return <Spinner size='l' type='brand' />
                                }
                                return [
                                    <Button
                                        key='empty-cart-button'
                                        className='icon-order'
                                        icon='cartPlus'
                                        type='success'
                                        variant='toggle'
                                    />,
                                    <span key='empty-cart-text'>{$t('cart.empty_title')}</span>,
                                ]
                            })()}
                        </div>
                    ) : (
                        cart_items
                            .toSorted((a, b) => {
                                const aName = a.product_name || ''
                                const bName = b.product_name || ''
                                return aName.localeCompare(bName)
                            })
                            .map((cart_item) => this.order_line(cart_item, true, vnode.attrs.variant !== 'wide'))
                    )}
                </div>
            </div>
        )
    }
}
